import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import './Analytics.css';

import Sidebarnew from "./NewUI/Sidebarnew";
import Navigationnew from "./NewUI/Navigationnew";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ✅ Topic pill colors
const topicColors = {
  'LEAVE & ATTENDANCE': { bg: '#EFF6FF', color: '#3B82F6' },
  'REMOTE WORK':        { bg: '#F0FDF4', color: '#16A34A' },
  'EXPENSE POLICY':     { bg: '#FFF7ED', color: '#D97706' },
  'HR POLICIES':        { bg: '#F5F3FF', color: '#7C3AED' },
  'COMPLIANCE':         { bg: '#FEF2F2', color: '#DC2626' },
  'TAX':                { bg: '#ECFDF5', color: '#059669' },
  'PAYROLL':            { bg: '#FFF1F2', color: '#E11D48' },
  'RAG Search':         { bg: '#F1F5F9', color: '#64748B' },
};

const getTopicStyle = (t) => topicColors[t] || { bg: '#F1F5F9', color: '#64748B' };

// ✅ Bar colors
const BAR_COLORS = ['#4B7BF5', '#7C3AED', '#06B6D4', '#16A34A', '#F59E0B', '#EF4444', '#8B5CF6', '#10B981'];

// ✅ Time ago helper
const getTimeAgo = (dateString) => {
  const diff = new Date() - new Date(dateString);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor(diff / (1000 * 60));
  
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'Just now';
};

const Analytics = () => {
  const navigate = useNavigate();
  const [selectedAgent, setSelectedAgent] = useState(null);

  // States
  const [engagementPeriod, setEngagementPeriod] = useState('Daily');
  const [engagementData, setEngagementData] = useState({
    labels: [], 
    values: [], 
    mostEngaged: 'Loading...', 
    leastEngaged: '-', 
    rawLength: 0
  });

  const [performanceAgents, setPerformanceAgents] = useState([]);
  const [selectedPerfAgent, setSelectedPerfAgent] = useState({ id: '', name: 'Select Agent' });
  const [performanceMetrics, setPerformanceMetrics] = useState({
    total: 0, rating: 0, attention: 0, time: "1.0s"
  });

  const [docMetrics, setDocMetrics] = useState({ total: 0, unused: 0 });
  const [queriesView, setQueriesView] = useState('Daily');
  const [dynamicTopQueries, setDynamicTopQueries] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState({ count: 0, docs: [] });

  // ================== FETCHES ==================
  useEffect(() => {
    const fetchGraph = async () => {
      try {
        const res = await api.get(`/analytics/engagement?period=${engagementPeriod.toLowerCase()}`);
        setEngagementData({
          labels: res.data.chartData?.map(d => d.agent) || [],
          values: res.data.chartData?.map(d => d.queries) || [],
          mostEngaged: res.data.mostEngaged || 'None',
          leastEngaged: res.data.leastEngaged || '-',
          rawLength: res.data.chartData?.length || 0
        });
      } catch (err) {
        console.error("Engagement Fetch Error:", err);
      }
    };
    fetchGraph();
  }, [engagementPeriod]);

  useEffect(() => {
    const initPerformance = async () => {
      try {
        const res = await api.get('/analytics/perf-init');
        if (res.data.agents?.length > 0) {
          setPerformanceAgents(res.data.agents);
          setSelectedPerfAgent(res.data.agents[0]);
          setPerformanceMetrics(res.data.stats);
        }
      } catch (err) {
        console.error("Performance Init Error:", err);
      }
    };
    initPerformance();
  }, []);

  useEffect(() => {
    if (!selectedPerfAgent.id) return;
    const fetchUpdate = async () => {
      try {
        const res = await api.get(`/analytics/perf-update/${selectedPerfAgent.id}`);
        setPerformanceMetrics(res.data);
      } catch (err) {
        console.error("Performance Update Error:", err);
      }
    };
    fetchUpdate();
  }, [selectedPerfAgent.id]);

  useEffect(() => {
    const fetchDocMetrics = async () => {
      try {
        const res = await api.get('/analytics/document-metrics');
        setDocMetrics({ total: res.data.total || 0, unused: res.data.unused || 0 });
      } catch (err) {
        console.error("Doc Metrics Error:", err);
      }
    };
    fetchDocMetrics();
  }, []);

  useEffect(() => {
    const fetchRecentUpdates = async () => {
      try {
        const res = await api.get('/analytics/recent-updates');
        setRecentUpdates({
          count: res.data.count || 0,
          docs: res.data.recentDocs || []
        });
      } catch (err) {
        console.error("Recent Updates Error:", err);
      }
    };
    fetchRecentUpdates();
  }, []);

  useEffect(() => {
    const fetchTopQueries = async () => {
      try {
        const res = await api.get(`/analytics/top-missed-queries?period=${queriesView.toLowerCase()}`);
        setDynamicTopQueries(res.data || []);
      } catch (err) {
        console.error("Top Queries Fetch Error:", err);
        setDynamicTopQueries([]);
      }
    };
    fetchTopQueries();
  }, [queriesView]);

  // Chart Data
  const engagementChartData = {
    labels: engagementData.labels,
    datasets: [{
      label: 'Queries',
      data: engagementData.values,
      backgroundColor: engagementData.labels.map((_, i) => BAR_COLORS[i % BAR_COLORS.length]),
      borderRadius: 6,
      maxBarThickness: 120,
    }]
  };

  const engagementChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
x: {
  grid: {
    display: false
  },

  ticks: {
    color: '#64748B',

    font: {
      size: 10,
      weight: '500'
    },

    maxRotation: 0,

    minRotation: 0,

    autoSkip: false,

    padding: 8
  }
},      y: { beginAtZero: true, grid: { color: '#F1F5F9' }, ticks: { font: { size: 11 }, color: '#64748B' } }
    }
  };

  return (
<div className="db-page-layout analytics-page">
        {/* SIDEBAR */}
      <aside className="db-sidebar-shell">
        <Sidebarnew selectedAgent={selectedAgent} />
      </aside>

      {/* MAIN WRAPPER */}
      <div className="db-main-wrapper">
        {/* TOP NAV */}
        <div className="db-topbar-shell">
          <Navigationnew
            selectedAgent={selectedAgent}
            setSelectedAgent={setSelectedAgent}
          />
        </div>

        {/* ANALYTICS CONTENT */}
        <div className="an-container">
          <div className="an-content">

            <div className="db-page-header">
              <h1 className="db-page-title">Analytics</h1>
              <p className="db-page-subtitle">Usage and performance analytics.</p>
            </div>

            {/* ROW 1 */}
            <div className="an-row">
              {/* LEFT COLUMN */}
              <div className="an-col-left">
                {/* Document Metrics */}
              {/* Document Metrics */}
                <div className="an-card mb-3 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                  <div className="an-card-header bg-white border-0 pt-4 pb-2">
                    <span className="an-history-title fs-6 fw-bold" style={{ color: '#1E293B' }}>Document Usage Metrics</span>
                  </div>
                  <div className="an-card-body d-flex flex-row align-items-center gap-3 w-100" style={{ padding: '0 18px 18px' }}>
                    
                    {/* Block 1: Total Documents */}
                    <div className="an-metric-row an-metric-row--transparent flex-grow-1 m-0">
                      <div className="an-perf-text me-auto">
                        <span className="an-perf-label text-muted">TOTAL DOCUMENTS</span>
                        <span className="an-perf-val-custom">{docMetrics.total.toLocaleString()}</span>
                      </div>
                      <img src="/images/docsheet.svg" alt="" className="an-metric-icon-doc" />
                    </div>

                    {/* Block 2: Never Referenced */}
                    <div className="an-metric-row an-metric-row--transparent flex-grow-1 m-0">
                      <div className="an-perf-text me-auto">
                        <span className="an-perf-label text-muted">NEVER REFERENCED</span>
                        <span className="an-perf-val-custom">{docMetrics.unused}</span>
                      </div>
                      <img src="/images/doccross.svg" alt="" className="an-metric-icon-doc" />
                    </div>

                    {/* Standalone View Button */}
                   <button
  className="an-view-link btn btn-primary fw-medium px-4 ms-1"
  onClick={() => navigate('/kb?linkStatus=unlinked')}
>
  View
</button>

                  </div>
                </div>

                {/* Agent Engagement */}
                <div className="an-card an-card--engagement">
                  <div className="an-card-header">
                    <span className="an-card-title">Agent Engagement</span>
                    <div className="an-period-tabs">
                      {['Daily', 'Weekly', 'Monthly'].map(p => (
                        <button
                          key={p}
                          className={`an-period-tab${engagementPeriod === p ? ' active' : ''}`}
                          onClick={() => setEngagementPeriod(p)}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="an-engagement-meta">
                    <span className="an-eng-item">
                      <img src="/images/mosteng.svg" alt="" className="an-eng-icon" />
                      <span className="an-eng-label">Most engaged:</span>
                      <span className="an-eng-val an-eng-val--green">
                        {engagementData.mostEngaged}
                      </span>
                    </span>
                    <span className="an-eng-item">
                      <img src="/images/dec.svg" alt="" className="an-eng-icon" />
                      <span className="an-eng-label">Least engaged:</span>
                      <span className="an-eng-val an-eng-val--red">
                        {engagementData.leastEngaged}
                      </span>
                    </span>
                  </div>
                  <div className="an-chart-wrap">
                    <Bar data={engagementChartData} options={engagementChartOptions} />
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN */}
             {/* RIGHT COLUMN */}
              <div className="an-col-right">
                <div className="an-card an-right-combined-card">
                  
                  {/* === Agent Performance Section === */}
                  <div className="an-card-header bg-white border-0 pt-4 pb-2">
                    <span className="an-history-title fs-6">Agent Performance</span>
                    <div className="dropdown">
                      <button className="an-agent-select dropdown-toggle bg-light border-0" type="button" data-bs-toggle="dropdown">
                        <span>{selectedPerfAgent.name}</span>
                      </button>
                      <ul className="dropdown-menu shadow-sm">
                        {performanceAgents.map(agent => (
                          <li key={agent.id}>
                            <button className="dropdown-item py-2" onClick={() => setSelectedPerfAgent(agent)}>
                              {agent.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
               <div className="an-card-body an-perf-body">
                    {/* Metric 1: Total Queries */}
                    <div className="an-perf-row">
                      <div className="an-perf-left">
                        <img src="/images/totalque.svg" alt="" className="an-metric-icon-large" />
                        <div className="an-perf-text">
                          <span className="an-perf-label">Total Queries</span>
                          <span className="an-perf-val-reduced">{performanceMetrics.total}</span>
                        </div>
                      </div>
                    </div>

                    {/* Metric 2: Avg Response Time */}
                    <div className="an-perf-row">
                      <div className="an-perf-left">
                        <img src="/images/clockingnew.svg" alt="" className="an-metric-icon-large" />
                        <div className="an-perf-text">
                          <span className="an-perf-label">Avg Response Time</span>
                          <span className="an-perf-val-reduced">{performanceMetrics.time}</span>
                        </div>
                      </div>
                    </div>

                    {/* Metric 3: Average Rating */}
                    <div className="an-perf-row">
                      <div className="an-perf-left">
                        <img src="/images/newavg.svg" alt="" className="an-metric-icon-large" />
                        <div className="an-perf-text">
                          <span className="an-perf-label">Average Rating</span>
                          <span className="an-perf-val-reduced">{performanceMetrics.rating}</span>
                        </div>
                      </div>
                    </div>

                    {/* Metric 4: Queries needing attention */}
                    <div className="an-perf-row border-0">
                      <div className="an-perf-left">
                        <img src="/images/newqueries.svg" alt="" className="an-metric-icon-large" />
                        <div className="an-perf-text">
                          <span className="an-perf-label">Queries needing attention</span>
                          <span className="an-perf-val-reduced">{performanceMetrics.attention}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* === Divider === */}
                  <div className="an-right-divider"></div>

                  {/* === Recent Update History Section === */}
                  <div className="an-history-header">
                    <span className="an-history-title">Recent Update History</span>
                    <span className="an-history-days">last 7 days</span>
                  </div>

                  <div className="an-history-body">
                    <div className="an-recent-doc-item">
                      <div className="an-recent-doc-name" style={{ color: '#10B981', fontWeight: '500' }}>
                        <i className="bi bi-file-earmark-text me-2"></i>
                        Documents updated in last 7 days
                      </div>
                      <div className="an-perf-val fs-5 text-dark">{recentUpdates.count}</div>
                    </div>

                    {recentUpdates.docs.map((doc, i) => (
                      <div className="an-recent-doc-item" key={i}>
                        <div className="an-recent-doc-name" style={{ color: '#64748B' }}>
                          <i className="bi bi-file-earmark-text text-primary me-2"></i>
                          {doc.file_name}
                        </div>
                        <div className="an-recent-doc-time">
                          {getTimeAgo(doc.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            </div>

            {/* ROW 2: Top Queries */}
            <div className="an-card an-queries-card">
              <div className="an-queries-header">
                <span className="an-card-title">Top 10 Queries needing attention</span>
                <div className="an-period-tabs">
                  {['Daily', 'Weekly', 'Monthly'].map(p => (
                    <button
                      key={p}
                      className={`an-period-tab${queriesView === p ? ' active' : ''}`}
                      onClick={() => setQueriesView(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="an-table-wrap">
                <table className="an-table">
                  <thead>
                    <tr>
                      <th style={{ width: '52px' }}>No.</th>
                      <th>Question</th>
                      <th>Agent</th>
                      <th>Topic</th>
                      <th>Related Document</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dynamicTopQueries.length > 0 ? (
                      dynamicTopQueries.map((query, index) => (
                        <tr key={query.id || index}>
                          <td><span className="an-row-num">{query.no || index + 1}</span></td>
                          <td className="an-td-question">
                            {query.question?.length > 140
                              ? query.question.substring(0, 140) + "..."
                              : query.question}
                          </td>
                          <td className="an-td-agent">{query.agent_name || 'Agent'}</td>
                          <td>
                            <span
                              className="an-topic-pill"
                              style={getTopicStyle(query.failure_type || 'RAG Search')}
                            >
                              {(query.failure_type || 'RAG Search').toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <span className="an-doc-link">
                              {query.related_docs || 'No documents linked'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="an-empty">
                          No queries needing attention for this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;